import { useState, useMemo } from 'react';
import { Calendar, Clock, Check, AlertCircle, Info, ShieldAlert, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { CRMDialogContent } from '../../crm/components/CRMDialogContent';
import { Button } from '../../components/ui/button';
import { managerAPI } from './useManagerData';
import { apiError } from '../../crm/context/CRMAuthContext';

const MARKET_DEFINITIONS = [
  { id: 'forex', name: 'Forex / Döviz Çiftleri', category: 'forex' },
  { id: 'crypto', name: 'Kripto Para Piyasası (7/24)', category: 'crypto' },
  { id: 'stocks_bist', name: 'Borsa İstanbul (BIST Pay)', category: 'bist' },
  { id: 'stocks_us', name: 'ABD Borsaları (NYSE / NASDAQ)', category: 'us' },
  { id: 'stocks_de', name: 'Almanya (XETR)', category: 'eu' },
  { id: 'stocks_fr', name: 'Fransa (Euronext Paris)', category: 'eu' },
  { id: 'stocks_nl', name: 'Hollanda (Euronext Amsterdam)', category: 'eu' },
  { id: 'stocks_ch', name: 'İsviçre (SIX)', category: 'eu' },
  { id: 'stocks_eu', name: 'Avrupa Hisseleri Genel', category: 'eu' },
  { id: 'commodities', name: 'Emtia Piyasaları (Altın, Petrol, CFD)', category: 'commodities' },
  { id: 'indices', name: 'Dünya Endeksleri (CFD)', category: 'indices' },
];

const CATEGORIES = [
  { key: 'all', label: 'Tüm Piyasalar (Hepsi)' },
  { key: 'forex', label: 'Döviz (FX)' },
  { key: 'crypto', label: 'Kripto' },
  { key: 'bist', label: 'BIST' },
  { key: 'us', label: 'ABD Pay' },
  { key: 'eu', label: 'Avrupa' },
  { key: 'commodities', label: 'Emtia' },
  { key: 'indices', label: 'Endeksler' },
];

export const HolidayModal = ({ officialHolidays = [], onClose, onCreated }) => {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [date, setDate] = useState(tomorrow);
  const [name, setName] = useState('');
  const [isFullDay, setIsFullDay] = useState(true);
  const [closeTime, setCloseTime] = useState('13:00');
  const [selectedMarkets, setSelectedMarkets] = useState(MARKET_DEFINITIONS.map(m => m.id));
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Conflict / correlation check with official exchange calendar
  const officialOnDate = useMemo(() => {
    return officialHolidays.filter(h => h.date === date);
  }, [officialHolidays, date]);

  const toggleMarket = (marketId) => {
    setSelectedMarkets(prev => 
      prev.includes(marketId) ? prev.filter(id => id !== marketId) : [...prev, marketId]
    );
  };

  const handleCategorySelect = (categoryKey) => {
    if (categoryKey === 'all') {
      if (selectedMarkets.length === MARKET_DEFINITIONS.length) {
        setSelectedMarkets([]);
      } else {
        setSelectedMarkets(MARKET_DEFINITIONS.map(m => m.id));
      }
      return;
    }
    const catMarkets = MARKET_DEFINITIONS.filter(m => m.category === categoryKey).map(m => m.id);
    const allSelected = catMarkets.every(id => selectedMarkets.includes(id));
    if (allSelected) {
      setSelectedMarkets(prev => prev.filter(id => !catMarkets.includes(id)));
    } else {
      setSelectedMarkets(prev => Array.from(new Set([...prev, ...catMarkets])));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Lütfen bir tatil veya kapanış adı girin.');
      return;
    }
    if (selectedMarkets.length === 0) {
      setError('Lütfen en az bir piyasa seçin.');
      return;
    }
    if (!isFullDay && !closeTime) {
      setError('Lütfen erken kapanış saatini belirtin.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      await managerAPI.post('/holidays', {
        date,
        name: name.trim(),
        markets: selectedMarkets,
        is_full_day: isFullDay,
        close_time: isFullDay ? null : closeTime,
        description: description.trim() || undefined,
      });
      toast.success('Piyasa tatili / erken kapanış takvimi kaydedildi.');
      await onCreated?.();
      onClose();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
      <CRMDialogContent className="crm-dialog holiday-dialog" data-testid="manager-holiday-modal" style={{ maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={20} color="#1d4ed8" />
            <DialogTitle>Piyasa Tatili &amp; Kapanış Tanımla</DialogTitle>
          </div>
          <DialogDescription>
            Piyasaların kapalı olacağı resmî/özel günleri veya erken seans kapanışlarını belirleyin.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' }}>
          {error && (
            <div className="crm-error" role="alert" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b', fontSize: '13px' }}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Date & Name */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 1fr) 2fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '5px' }}>
                Tarih
              </label>
              <input
                type="date"
                required
                value={date}
                disabled={busy}
                onChange={e => setDate(e.target.value)}
                style={{ width: '100%', height: '38px', padding: '6px 10px', border: '1px solid var(--crm-line)', borderRadius: '6px', background: 'var(--crm-surface)', color: 'var(--crm-ink)', fontSize: '13px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '5px' }}>
                Tatil / Kapanış Adı
              </label>
              <input
                type="text"
                required
                placeholder="Örn: Cumhuriyet Bayramı, Noel Tatili, Sistem Bakımı..."
                value={name}
                disabled={busy}
                onChange={e => setName(e.target.value)}
                style={{ width: '100%', height: '38px', padding: '6px 10px', border: '1px solid var(--crm-line)', borderRadius: '6px', background: 'var(--crm-surface)', color: 'var(--crm-ink)', fontSize: '13px' }}
              />
            </div>
          </div>

          {/* Official Calendar Correlation / Conflict Info */}
          {officialOnDate.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 14px', borderRadius: '6px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: '12px' }}>
              <Info size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
              <div>
                <strong>Resmî Borsa Takvimi Bilgisi ({date}):</strong>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
                  {officialOnDate.map((item, idx) => (
                    <li key={idx}>
                      {item.source}: {item.name} {item.close_time ? `(Erken Kapanış: ${item.close_time})` : '(Tam Gün Kapalı)'}
                    </li>
                  ))}
                </ul>
                <span style={{ fontSize: '11px', color: '#3b82f6', display: 'block', marginTop: '4px' }}>
                  ✓ Tanımladığınız tatil bu takvimle çakışmaz; piyasa saatlerini entegre olarak yönetir.
                </span>
              </div>
            </div>
          )}

          {/* Holiday Type & Time */}
          <div style={{ padding: '12px', border: '1px solid var(--crm-line)', borderRadius: '8px', background: 'var(--crm-subtle)' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '8px' }}>
              Kapanış Türü
            </label>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="holiday_type"
                  checked={isFullDay}
                  disabled={busy}
                  onChange={() => setIsFullDay(true)}
                />
                <span>Tam Gün Kapalı (24 Saat)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="holiday_type"
                  checked={!isFullDay}
                  disabled={busy}
                  onChange={() => setIsFullDay(false)}
                />
                <span>Erken Kapanış / Yarım Gün</span>
              </label>

              {!isFullDay && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                  <Clock size={16} color="#d97706" />
                  <span style={{ fontSize: '12px', color: 'var(--crm-muted)' }}>Kapanış Saati:</span>
                  <input
                    type="time"
                    required
                    value={closeTime}
                    disabled={busy}
                    onChange={e => setCloseTime(e.target.value)}
                    style={{ height: '32px', padding: '4px 8px', border: '1px solid var(--crm-line)', borderRadius: '4px', background: 'var(--crm-surface)', fontSize: '13px' }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Market Selection & Categories */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-muted)' }}>
                Uygulanacak Piyasalar ({selectedMarkets.length} / {MARKET_DEFINITIONS.length} Seçili)
              </label>
            </div>

            {/* Category Quick Select Buttons */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {CATEGORIES.map(cat => {
                const isAll = cat.key === 'all';
                const isCatAll = isAll
                  ? selectedMarkets.length === MARKET_DEFINITIONS.length
                  : MARKET_DEFINITIONS.filter(m => m.category === cat.key).every(m => selectedMarkets.includes(m.id));
                return (
                  <button
                    key={cat.key}
                    type="button"
                    disabled={busy}
                    onClick={() => handleCategorySelect(cat.key)}
                    style={{
                      padding: '4px 10px',
                      fontSize: '11px',
                      fontWeight: 500,
                      borderRadius: '4px',
                      border: isCatAll ? '1px solid #2563eb' : '1px solid var(--crm-line)',
                      background: isCatAll ? '#eff6ff' : 'var(--crm-surface)',
                      color: isCatAll ? '#1d4ed8' : 'var(--crm-ink)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {isCatAll && <Check size={11} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-1px' }} />}
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Market Checkboxes Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', maxHeight: '180px', overflowY: 'auto', padding: '10px', border: '1px solid var(--crm-line)', borderRadius: '6px', background: 'var(--crm-surface)' }}>
              {MARKET_DEFINITIONS.map(m => {
                const checked = selectedMarkets.includes(m.id);
                return (
                  <label
                    key={m.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      background: checked ? 'rgba(37,99,235,0.06)' : 'transparent',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={busy}
                      onChange={() => toggleMarket(m.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: checked ? 600 : 400, color: checked ? 'var(--crm-ink)' : 'var(--crm-muted)' }}>
                      {m.name}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '5px' }}>
              Açıklama / Not (İsteğe Bağlı)
            </label>
            <input
              type="text"
              placeholder="Kullanıcılara veya personele gösterilecek bilgi notu..."
              value={description}
              disabled={busy}
              onChange={e => setDescription(e.target.value)}
              style={{ width: '100%', height: '36px', padding: '6px 10px', border: '1px solid var(--crm-line)', borderRadius: '6px', background: 'var(--crm-surface)', color: 'var(--crm-ink)', fontSize: '13px' }}
            />
          </div>

          {/* Dialog Actions */}
          <div className="crm-dialog-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px', paddingTop: '12px', borderTop: '1px solid var(--crm-line)' }}>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              Vazgeç
            </Button>
            <Button type="submit" className="crm-primary" disabled={busy} style={{ minWidth: '120px' }}>
              {busy ? 'Kaydediliyor…' : 'Tatili Kaydet'}
            </Button>
          </div>
        </form>
      </CRMDialogContent>
    </Dialog>
  );
};
